// ============================================
// SELECTORICONOLUCIDE.JS — Selector visual de iconos Lucide (PORT-7)
// ============================================
// Reemplaza el campo de texto de icono por una galeria con clic. El valor que
// guarda/devuelve sigue siendo el NOMBRE del icono en kebab-case (ej:
// 'user-check'), igual que antes, para no romper datos existentes ni el backend.
//
// Uso:
//   <SelectorIconoLucide value={icono} onChange={(nombre) => setIcono(nombre)} />
//
// Estrategia de rendimiento: por defecto muestra una lista CURADA (~130 iconos
// utiles para estados de negocio). La barra de busqueda filtra sobre el set
// COMPLETO de lucide-react, capando los resultados para que abra rapido y no
// congele la UI.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as LucideIcons from 'lucide-react';

const PP = "'Poppins', sans-serif";

const colores = {
  principal: '#5c9adb',
  oscuro:    '#2f5f9e',
  gris:      '#4e5357',
  grisClaro: '#5e666e',
  fondo:     '#f3f4f4',
  blanco:    '#ffffff',
  borde:     '#e0e3e6',
};

// Cuantos resultados como maximo se pintan al buscar (evita congelar con cientos).
const MAX_RESULTADOS = 150;

// Lista curada de iconos comunes para estados de negocio (nombres de componente
// PascalCase de lucide-react). Si alguno no existe en la version instalada, se
// filtra en runtime sin romper.
const CURADOS = [
  'Sparkles', 'Star', 'Flag', 'FlagTriangleRight', 'Bookmark', 'Tag', 'Tags',
  'Check', 'CheckCircle', 'CheckCircle2', 'CircleCheck', 'CircleDot', 'Circle',
  'Clock', 'Clock3', 'Timer', 'Hourglass', 'Calendar', 'CalendarCheck', 'CalendarClock',
  'Bell', 'BellRing', 'Mail', 'MailCheck', 'Send', 'MessageSquare', 'MessagesSquare',
  'Phone', 'PhoneCall', 'User', 'UserCheck', 'UserPlus', 'Users', 'UserCog',
  'Briefcase', 'Building', 'Building2', 'Factory', 'Store', 'Home', 'MapPin', 'Map',
  'Truck', 'Package', 'PackageCheck', 'PackageOpen', 'Box', 'Boxes',
  'ShoppingCart', 'ShoppingBag', 'ClipboardList', 'ClipboardCheck', 'Clipboard',
  'FileText', 'FileCheck', 'File', 'Files', 'Folder', 'FolderOpen', 'FolderCheck',
  'Pencil', 'PenTool', 'Settings', 'Wrench', 'Hammer', 'Cog',
  'Camera', 'Video', 'Cctv', 'Network', 'Wifi', 'Cable', 'Plug', 'Zap', 'Lightbulb',
  'Target', 'Crosshair', 'TrendingUp', 'TrendingDown', 'BarChart', 'BarChart2',
  'LineChart', 'PieChart', 'Activity', 'Award', 'Trophy', 'Medal',
  'ThumbsUp', 'ThumbsDown', 'Heart', 'Eye', 'Search', 'Filter',
  'Key', 'Lock', 'Unlock', 'Shield', 'ShieldCheck',
  'AlertCircle', 'AlertTriangle', 'Info', 'HelpCircle', 'XCircle', 'X', 'Ban',
  'Pause', 'Play', 'Square', 'RefreshCw', 'RotateCw', 'Repeat',
  'ArrowRight', 'ArrowUpRight', 'ChevronRight', 'CornerDownRight',
  'GitBranch', 'Workflow', 'Layers', 'List', 'ListChecks', 'LayoutGrid', 'Inbox', 'Archive', 'Trash2',
  'DollarSign', 'CircleDollarSign', 'CreditCard', 'Wallet', 'Receipt', 'Banknote', 'Coins', 'Percent', 'HandCoins', 'Handshake',
  'Rocket', 'Flame', 'Snowflake', 'Sun', 'Moon', 'Cloud', 'Droplet', 'Leaf', 'TreePine',
];

// PascalCase -> kebab-case (UserCheck -> user-check, Building2 -> building-2).
function aKebab(pascal) {
  return String(pascal || '')
    .replace(/([a-zA-Z])([0-9])/g, '$1-$2')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();
}

// kebab/snake -> PascalCase (user-check -> UserCheck) para resolver el componente.
function aPascal(nombre) {
  return String(nombre || '')
    .trim()
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join('');
}

// True si una clave exportada por lucide-react es un icono real (y no un helper
// o un alias). Los iconos son PascalCase; excluimos utilidades conocidas y los
// alias con prefijo 'Lucide' o sufijo 'Icon'.
const EXCLUIR = new Set(['Icon', 'createLucideIcon', 'icons', 'default', 'LucideProps']);
function esNombreIcono(clave) {
  if (EXCLUIR.has(clave)) return false;
  if (clave.startsWith('Lucide')) return false;       // alias LucideCamera, etc.
  if (clave.endsWith('Icon')) return false;           // alias *Icon
  if (!/^[A-Z][A-Za-z0-9]*$/.test(clave)) return false;
  return true;
}

// Set completo de nombres de icono validos (PascalCase), calculado una sola vez.
const TODOS_PASCAL = Object.keys(LucideIcons).filter(esNombreIcono);

// Render seguro de un icono por nombre (kebab o pascal). Si no resuelve, null.
function Icono({ nombre, size = 20, color = colores.gris }) {
  const Ico = nombre ? LucideIcons[aPascal(nombre)] : null;
  if (!Ico) return null;
  return <Ico size={size} color={color} strokeWidth={2} style={{ flexShrink: 0 }} />;
}

// Resolver REUTILIZABLE para pintar iconos en listas (Workflows, portafolios, etc.).
// Convierte el nombre kebab/pascal a su componente Lucide; si el nombre esta vacio
// o no existe, cae a un placeholder neutro (ImagePlus), NUNCA a una carpeta forzada.
export function IconoLucide({ nombre, size = 18, color = colores.gris }) {
  const Ico = (nombre && LucideIcons[aPascal(nombre)]) || LucideIcons.ImagePlus;
  return <Ico size={size} color={color} strokeWidth={2} style={{ flexShrink: 0 }} />;
}

export default function SelectorIconoLucide({ value, onChange }) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const inputRef = useRef(null);

  // Cerrar con Escape mientras esta abierto.
  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [abierto]);

  // Enfocar la busqueda al abrir.
  useEffect(() => {
    if (abierto && inputRef.current) inputRef.current.focus();
  }, [abierto]);

  // Lista a mostrar: curada por defecto; al buscar, filtra el set completo.
  const { lista, total } = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) {
      const curados = CURADOS.filter(p => LucideIcons[p]).map(aKebab);
      return { lista: curados, total: curados.length };
    }
    const matches = [];
    for (const pascal of TODOS_PASCAL) {
      const kebab = aKebab(pascal);
      if (kebab.includes(q)) {
        matches.push(kebab);
        if (matches.length >= MAX_RESULTADOS) break;
      }
    }
    return { lista: matches, total: matches.length };
  }, [busqueda]);

  const valido = value && LucideIcons[aPascal(value)];

  const elegir = (nombre) => {
    onChange(nombre);
    setAbierto(false);
    setBusqueda('');
  };

  return (
    <>
      {/* Disparador */}
      <button
        type="button"
        onClick={() => setAbierto(true)}
        aria-label="Elegir icono"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          width: '100%', boxSizing: 'border-box', padding: '9px 11px',
          border: `1px solid ${colores.borde}`, borderRadius: 8,
          background: colores.blanco, cursor: 'pointer', fontFamily: PP,
          fontSize: 14, color: colores.gris, textAlign: 'left',
        }}
      >
        <span style={{
          width: 30, height: 30, borderRadius: 7, flexShrink: 0,
          background: colores.fondo, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {valido ? <Icono nombre={value} size={18} color={colores.oscuro} /> : <LucideIcons.ImagePlus size={16} color={colores.grisClaro} />}
        </span>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {valido ? value : (value ? `${value} (no encontrado)` : 'Elegir icono...')}
        </span>
        <LucideIcons.ChevronDown size={16} color={colores.grisClaro} />
      </button>

      {/* Popover / galeria.
          createPortal a document.body: el modal de estado padre usa
          transform: translate(...), y un position:fixed dentro de un ancestro con
          transform se posiciona RELATIVO a ese ancestro (no al viewport), lo que
          sacaba el popover de la pantalla. Renderizarlo en el body lo libera. */}
      {abierto && createPortal(
        <>
          <div onClick={() => setAbierto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500 }} />
          <div
            role="dialog"
            aria-label="Galeria de iconos"
            style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              background: colores.blanco, borderRadius: 12, zIndex: 501,
              width: 'min(420px, calc(100vw - 32px))', maxHeight: 'min(70vh, 560px)',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)', fontFamily: PP, overflow: 'hidden',
            }}
          >
            {/* Cabecera + busqueda */}
            <div style={{ padding: '16px 16px 10px', borderBottom: `1px solid ${colores.borde}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <strong style={{ fontSize: 15, color: colores.oscuro }}>Elegir icono</strong>
                <button type="button" onClick={() => setAbierto(false)} aria-label="Cerrar" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colores.gris, padding: 4 }}>
                  <LucideIcons.X size={18} />
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${colores.borde}`, borderRadius: 8, padding: '8px 10px' }}>
                <LucideIcons.Search size={16} color={colores.grisClaro} />
                <input
                  ref={inputRef}
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar icono por nombre (ej: truck, check, clock)"
                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, fontFamily: PP, color: colores.gris, background: 'transparent' }}
                />
                {value && (
                  <button type="button" onClick={() => elegir('')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colores.grisClaro, fontSize: 12, whiteSpace: 'nowrap' }}>
                    Quitar
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: colores.grisClaro, marginTop: 6 }}>
                {busqueda.trim() ? `${total} resultado(s)${total >= MAX_RESULTADOS ? '+ (afina la busqueda)' : ''}` : `${total} iconos comunes - escribe para buscar entre todos`}
              </div>
            </div>

            {/* Cuadricula (scroll interno; el titulo y el buscador quedan fijos arriba) */}
            <div style={{ flex: 1, minHeight: 0, padding: 12, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 6, alignContent: 'start' }}>
              {lista.map(nombre => {
                const seleccionado = nombre === value;
                return (
                  <button
                    key={nombre}
                    type="button"
                    onClick={() => elegir(nombre)}
                    title={nombre}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      padding: '10px 4px', borderRadius: 8, cursor: 'pointer',
                      background: seleccionado ? '#e8f0fb' : colores.blanco,
                      border: seleccionado ? `2px solid ${colores.principal}` : `1px solid ${colores.borde}`,
                    }}
                  >
                    <Icono nombre={nombre} size={22} color={seleccionado ? colores.oscuro : colores.gris} />
                    <span style={{ fontSize: 10, color: colores.grisClaro, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nombre}
                    </span>
                  </button>
                );
              })}
              {lista.length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: 30, textAlign: 'center', color: colores.grisClaro, fontSize: 13 }}>
                  No hay iconos que coincidan con "{busqueda}".
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
